import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { financeApi, formatCents } from "@/services/finance";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";

const m = (c: number) => formatCents(c);  // ⚠️-1 到分·与全站财务页统一
const MONTHS = ["2026-01", "2026-02", "2026-03", "2026-04", "2026-05"];
const SEL = "h-9 rounded-lg border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-700 focus:border-blue-500 focus:outline-none";

/** 财务报表 + 渠道分析（/admin/finance/reports · canvas page-76 · 全 demo·方案A）。 */
export function ReportsPage() {
  const [period, setPeriod] = useState("2026-05");
  const q = useQuery({ queryKey: ["finance", "reports-full", period], queryFn: () => financeApi.fullReport(period) });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return (
    <div className="flex flex-col items-center justify-center gap-2 p-12">
      <p className="text-[13px] font-semibold text-red-700">财务报表加载失败</p>
      <p className="text-[11px] text-red-500">{(q.error as Error)?.message || "网络错误"}</p>
      <button onClick={() => q.refetch()} disabled={q.isFetching} className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{q.isFetching ? "重试中…" : "点击重试"}</button>
    </div>
  );
  const r = q.data;
  const pl = r?.pl;
  const cf = r?.cashflow;
  const maxNet = Math.max(1, ...(r?.product_ranking ?? []).map((p) => p.net_cents), ...(r?.channels ?? []).map((c) => c.net_cents));

  return (
    <PageShell
      title="📊 财务报表 + 渠道分析"
      subtitle="月度 P&L · 渠道毛利对比 · 产品排行 · 现金流报表 · 币种 USD"
      actions={
        <div className="flex gap-2">
          <span className="flex h-9 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-[12px] font-medium text-slate-600">USD</span>
          <select className={SEL} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {MONTHS.map((mo) => <option key={mo} value={mo}>{mo} 报表</option>)}
          </select>
          <button type="button" disabled title="规划中" className="flex h-9 cursor-not-allowed items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-400 opacity-60">导出 Excel（规划中）</button>
        </div>
      }
    >
      <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">🔒 仅老板 + 财务全权限 · 经理无访问</div>

      {/* 1 · 月度 P&L */}
      <Card title={`📈 月度 P&L 报表（${pl?.period ?? ""}）`} action={<span className="text-[12px] text-slate-500">损益表 · 自动生成 · demo 数据</span>}>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <tbody>
              <PLGroup label="一、营业收入合计" cents={pl?.revenue_cents ?? 0} pct={100} color="text-emerald-500" />
              {pl?.income_lines.map((l) => <PLSub key={l.label} l={l} color="text-emerald-600" />)}
              <PLGroup label="二、营业成本合计" cents={pl?.cost_cents ?? 0} pct={pl ? +((pl.cost_cents / pl.revenue_cents) * 100).toFixed(1) : 0} color="text-red-500" />
              {pl?.cost_lines.map((l) => <PLSub key={l.label} l={l} color="text-red-500" />)}
              <PLGroup label="毛利润" cents={pl?.gross_profit_cents ?? 0} pct={pl?.gross_margin_pct ?? 0} color={(pl?.gross_profit_cents ?? 0) < 0 ? "text-red-600" : "text-blue-500"} />
              <PLGroup label="三、营业费用" cents={pl?.opex_cents ?? 0} pct={pl ? +((pl.opex_cents / pl.revenue_cents) * 100).toFixed(1) : 0} color="text-red-500" />
              {pl?.opex_lines.map((l) => <PLSub key={l.label} l={l} color="text-red-500" />)}
              <PLGroup label="净利润" cents={pl?.net_profit_cents ?? 0} pct={pl?.net_margin_pct ?? 0} color={(pl?.net_profit_cents ?? 0) < 0 ? "text-red-600" : "text-emerald-500"} strong />
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 2 · 4 渠道毛利对比 */}
        <Card title="🎯 4 渠道毛利对比" action={<span className="text-[11px] text-slate-400">mirror 售价管理中心 · markup×GMV-扣点=净利</span>}>
          <div className="flex flex-col gap-2">
            {r?.channels.map((c) => (
              <div key={c.label} className="flex items-center gap-3">
                <span className="w-36 shrink-0 text-[12px] font-medium text-slate-700">{c.label} ({c.markup_pct}%)</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100"><div className="h-full rounded bg-emerald-400" style={{ width: `${(c.net_cents / maxNet) * 100}%` }} /></div>
                <span className="w-16 shrink-0 text-right text-[13px] font-bold text-emerald-600">{m(c.net_cents)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-slate-400">💡 B2B 单笔利润率低但总量大 · API 单笔利润中等</div>
        </Card>

        {/* 3 · 产品毛利排行 */}
        <Card title="🏆 产品毛利排行（前 5）" action={<span className="text-[11px] text-slate-400">按本月净利贡献排序</span>}>
          <div className="flex flex-col gap-2">
            {r?.product_ranking.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <span className="w-6 shrink-0 text-[12px] font-bold text-slate-400">{i + 1}</span>
                <span className="w-44 shrink-0 truncate text-[12px] font-medium text-slate-700">{p.name} · {p.groups}</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100"><div className="h-full rounded bg-blue-400" style={{ width: `${(p.net_cents / maxNet) * 100}%` }} /></div>
                <span className="w-16 shrink-0 text-right text-[13px] font-bold text-blue-600">{m(p.net_cents)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-slate-400">💡 主推产品占月利润 80% · 长尾 20%</div>
        </Card>
      </div>

      {/* 4 · 现金流报表（间接法） */}
      <Card title="💵 现金流报表（间接法）" action={<span className="text-[12px] text-slate-500">3 类活动：经营 / 投资 / 筹资</span>}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <CFBlock title="📊 经营活动" lines={cf?.operating ?? []} subtotal={cf?.op_subtotal_cents ?? 0} />
          <CFBlock title="🏢 投资活动" lines={cf?.investing ?? []} subtotal={cf?.inv_subtotal_cents ?? 0} />
          <CFBlock title="💼 筹资活动" lines={cf?.financing ?? []} subtotal={cf?.fin_subtotal_cents ?? 0} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-4 py-2 text-[12px]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-bold text-slate-900">🎯 本月净现金流：<span className={cf && cf.net_cents >= 0 ? "text-emerald-600" : "text-red-500"}>{m(cf?.net_cents ?? 0)}</span></span>
            <span className="text-slate-500">期初 {m(cf?.opening_cents ?? 0)} → 期末 {m(cf?.closing_cents ?? 0)}</span>
          </div>
          {/* 反链接：现金紧张 → 直接去催收逾期应收回笼现金（复用 ?overdue=1 深链） */}
          <Link to="/admin/finance/receivables?overdue=1" className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-amber-600">💰 去催款回笼现金 →</Link>
        </div>
      </Card>

    </PageShell>
  );
}

function PLGroup({ label, cents, pct, color, strong }: { label: string; cents: number; pct: number; color: string; strong?: boolean }) {
  return (
    <tr className={`border-t border-slate-100 ${strong ? "bg-slate-50" : ""}`}>
      <td className="px-4 py-2.5 text-[13px] font-bold text-slate-900">{label}</td>
      <td className={`px-4 py-2.5 text-right text-[14px] font-bold ${color}`}>{m(cents)}</td>
      <td className="w-20 px-4 py-2.5 text-right text-[11px] text-slate-500">{pct}%</td>
    </tr>
  );
}
function PLSub({ l, color }: { l: { label: string; cents: number; pct: number }; color: string }) {
  return (
    <tr className="border-t border-slate-50">
      <td className="px-4 py-2 pl-8 text-[12px] text-slate-700">{l.label}</td>
      <td className={`px-4 py-2 text-right text-[13px] font-semibold ${color}`}>{m(l.cents)}</td>
      <td className="px-4 py-2 text-right text-[11px] text-slate-400">{l.pct}%</td>
    </tr>
  );
}
function CFBlock({ title, lines, subtotal }: { title: string; lines: { label: string; cents: number }[]; subtotal: number }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="text-[12px] font-bold text-slate-900">{title}</div>
      <div className="mt-1.5 space-y-1 text-[11px]">
        {lines.map((l) => (
          <div key={l.label} className="flex justify-between"><span className="text-slate-600">{l.label}</span><span className={l.cents >= 0 ? "text-emerald-600" : "text-red-500"}>{m(l.cents)}</span></div>
        ))}
      </div>
      <div className="mt-2 flex justify-between border-t border-slate-100 pt-1.5 text-[12px] font-bold"><span className="text-slate-700">小计</span><span className={subtotal >= 0 ? "text-emerald-600" : "text-red-500"}>{m(subtotal)}</span></div>
    </div>
  );
}
